// admin/src/services/adminApi.js

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://tripsie.in/api";

class AdminApiService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/admin/auth`;
  }

  getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('adminToken');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  getAuthHeader() {
    const headers = {};
    const token = localStorage.getItem('adminToken');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    console.log('🔵 API Request:', options.method || 'GET', url);
    const config = { ...options, headers: this.getHeaders() };
    try {
      const response = await fetch(url, config);
      const data = await response.json();
      console.log('🟢 API Response:', response.status, data);
      if (!response.ok) {
        console.log('🔴 API Error:', response.status, data);
        const error = new Error(data.message || 'Request failed');
        error.response = { data, status: response.status };
        throw error;
      }
      return data;
    } catch (error) {
      console.log('🔴 API Catch:', error);
      if (error.response) throw error;
      const networkError = new Error('Network error. Please check your connection.');
      networkError.response = { data: { message: 'Network error. Please check your connection.' }, status: 0 };
      throw networkError;
    }
  }

  async heroRequest(url, options = {}) {
    console.log('🔵 Resource API:', options.method || 'GET', url);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.log('🔴 Non-JSON response:', response.status, text.substring(0, 500));
        if (response.status === 413) {
          data = { message: 'File too large. Nginx limit exceeded. Ask your server admin to increase client_max_body_size.' };
        } else if (text.includes('<html') || text.includes('<!DOCTYPE')) {
          data = { message: `Server returned HTML error (${response.status}). Check Nginx/server logs.` };
        } else {
          data = { message: `Server error (${response.status}): ${text.substring(0, 200)}` };
        }
      }
      console.log('🟢 Resource Response:', response.status, data);
      if (!response.ok) {
        const error = new Error(data.message || 'Request failed');
        error.response = { data, status: response.status };
        throw error;
      }
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.response) throw error;
      console.log('🔴 Fetch error:', error.name, error.message);
      let errorMessage = 'Network error. Please check your connection.';
      if (error.name === 'AbortError') {
        errorMessage = 'Upload timed out (>2 min). Try with smaller images or fewer files.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to server. Please check if the server is running.';
      } else if (error.message.includes('NetworkError')) {
        errorMessage = 'Network error during upload. Check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      const networkError = new Error(errorMessage);
      networkError.response = { data: { message: errorMessage }, status: 0 };
      throw networkError;
    }
  }

  // ════════════════════════════════════════
  // AUTH METHODS
  // ════════════════════════════════════════

  async login(email, password) {
    return this.request('/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  }
  async setup2FA(tempToken) {
    return this.request('/2fa/setup', { method: 'POST', body: JSON.stringify({ tempToken }) });
  }
  async verify2FA(tempToken, code) {
    return this.request('/2fa/verify', { method: 'POST', body: JSON.stringify({ tempToken, code }) });
  }
  async forgotPassword(email) {
    return this.request('/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  }
  async verifyOTP(email, otp) {
    return this.request('/verify-otp', { method: 'POST', body: JSON.stringify({ email, otp }) });
  }
  async resetPassword(resetToken, newPassword, confirmPassword) {
    return this.request('/reset-password', { method: 'POST', body: JSON.stringify({ resetToken, newPassword, confirmPassword }) });
  }
  async changePassword(currentPassword, newPassword, confirmPassword) {
    return this.request('/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword, confirmPassword }) });
  }
  async logout()      { return this.request('/logout',  { method: 'POST' }); }
  async getMe()       { return this.request('/me',      { method: 'GET'  }); }
  async verifyToken() { return this.request('/verify',  { method: 'GET'  }); }

  // ════════════════════════════════════════
  // HERO SLIDE METHODS
  // ════════════════════════════════════════

  async getHeroSlides() {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/slides`, { method: 'GET', headers: this.getHeaders() });
  }
  async getActiveHeroSlides() {
    return this.heroRequest(`${API_BASE_URL}/hero/slides`, { method: 'GET' });
  }
  async createHeroSlide(formData) {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/slides`, { method: 'POST', headers: this.getAuthHeader(), body: formData });
  }
  async updateHeroSlide(id, formData) {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/slides/${id}`, { method: 'PUT', headers: this.getAuthHeader(), body: formData });
  }
  async deleteHeroSlide(id) {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/slides/${id}`, { method: 'DELETE', headers: this.getHeaders() });
  }
  async toggleHeroSlide(id) {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/slides/${id}/toggle`, { method: 'PATCH', headers: this.getHeaders() });
  }
  async reorderHeroSlides(slideIds) {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/reorder`, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify({ slideIds }) });
  }
  async resetHeroSlides() {
    return this.heroRequest(`${API_BASE_URL}/hero/admin/reset`, { method: 'DELETE', headers: this.getHeaders() });
  }

  // ════════════════════════════════════════
  // TRIP MANAGEMENT METHODS
  // ════════════════════════════════════════

  async getTrips() {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/all`, { method: 'GET', headers: this.getHeaders() });
  }
  async getTripById(id) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/${id}`, { method: 'GET', headers: this.getHeaders() });
  }
  async getPublicTrips() {
    return this.heroRequest(`${API_BASE_URL}/trips/public/all`, { method: 'GET' });
  }
  async getPublicTripById(tripId) {
    return this.heroRequest(`${API_BASE_URL}/trips/public/${tripId}`, { method: 'GET' });
  }
  async createTrip(formData) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin`, { method: 'POST', headers: this.getAuthHeader(), body: formData });
  }
  async updateTrip(id, formData) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/${id}`, { method: 'PUT', headers: this.getAuthHeader(), body: formData });
  }
  async deleteTrip(id) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/${id}`, { method: 'DELETE', headers: this.getHeaders() });
  }
  async toggleTrip(id) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/${id}/toggle`, { method: 'PATCH', headers: this.getHeaders() });
  }
  async reorderTrips(tripIds) {
    return this.heroRequest(`${API_BASE_URL}/trips/admin/reorder`, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify({ tripIds }) });
  }

  // ════════════════════════════════════════
  // BOOKING MANAGEMENT METHODS
  // ════════════════════════════════════════

  async getBookings(status = 'all') {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/all?status=${status}`, { method: 'GET', headers: this.getHeaders() });
  }
  async getBookingStats() {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/stats`, { method: 'GET', headers: this.getHeaders() });
  }
  async getBookingById(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}`, { method: 'GET', headers: this.getHeaders() });
  }
  async updateBookingStatus(id, status, cancelReason = '') {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}/status`, { method: 'PATCH', headers: this.getHeaders(), body: JSON.stringify({ status, cancelReason }) });
  }
  async markBookingRead(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}/read`, { method: 'PATCH', headers: this.getHeaders() });
  }
  async deleteBooking(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}`, { method: 'DELETE', headers: this.getHeaders() });
  }

  // ════════════════════════════════════════
  // ★ REVIEW METHODS
  // ════════════════════════════════════════

  async getReviews(status = 'all') {
    return this.heroRequest(`${API_BASE_URL}/reviews/admin/all?status=${status}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
  }
  async approveReview(id) {
    return this.heroRequest(`${API_BASE_URL}/reviews/admin/${id}/approve`, {
      method: 'PATCH',
      headers: this.getHeaders(),
    });
  }
  async rejectReview(id, reason = '') {
    return this.heroRequest(`${API_BASE_URL}/reviews/admin/${id}/reject`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ reason }),
    });
  }

  // ════════════════════════════════════════
  // ★ RECYCLE BIN METHODS
  // ════════════════════════════════════════

  /**
   * PATCH /bookings/admin/:id/trash
   * Soft-deletes a booking → moves it to the recycle bin.
   * Backend should set: trashedAt = now, isTrashed = true
   * and remove it from the user's visible booking history.
   */
  async softDeleteBooking(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}/trash`, {
      method: 'PATCH',
      headers: this.getHeaders(),
    });
  }

  /**
   * GET /bookings/admin/recycle-bin
   * Returns all soft-deleted bookings, sorted by trashedAt desc.
   */
  async getRecycleBin() {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/recycle-bin`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
  }

  /**
   * PATCH /bookings/admin/:id/restore
   * Restores a booking from the recycle bin.
   * Backend should clear trashedAt / isTrashed and
   * re-add it to the user's booking history.
   */
  async restoreBooking(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}/restore`, {
      method: 'PATCH',
      headers: this.getHeaders(),
    });
  }

  /**
   * DELETE /bookings/admin/:id/permanent
   * Permanently deletes a booking and cascades to remove it
   * from the linked user's booking history on the backend.
   */
  async permanentDeleteBooking(id) {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/${id}/permanent`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
  }

  /**
   * DELETE /bookings/admin/recycle-bin/empty
   * Permanently deletes ALL trashed bookings in one shot.
   */
  async emptyRecycleBin() {
    return this.heroRequest(`${API_BASE_URL}/bookings/admin/recycle-bin/empty`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
  }

  // ════════════════════════════════════════
  // ENJOY TRAVEL METHODS
  // ════════════════════════════════════════

  async getEnjoyTravelData() {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/slides`, { method: 'GET', headers: this.getHeaders() });
  }
  async updateEnjoyTravelConfig(data) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/config`, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify(data) });
  }
  async createEnjoyTravelSlide(formData) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/slides`, { method: 'POST', headers: this.getAuthHeader(), body: formData });
  }
  async updateEnjoyTravelSlide(id, formData) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/slides/${id}`, { method: 'PUT', headers: this.getAuthHeader(), body: formData });
  }
  async deleteEnjoyTravelSlide(id) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/slides/${id}`, { method: 'DELETE', headers: this.getHeaders() });
  }
  async toggleEnjoyTravelSlide(id) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/slides/${id}/toggle`, { method: 'PATCH', headers: this.getHeaders() });
  }
  async reorderEnjoyTravelSlides(slideIds) {
    return this.heroRequest(`${API_BASE_URL}/enjoy/admin/reorder`, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify({ slideIds }) });
  }

  // ════════════════════════════════════════
  // BLOG TRAVEL METHODS
  // ════════════════════════════════════════

  async getBlogTravelAdmin() {
    return this.heroRequest(`${API_BASE_URL}/blog/admin`, { method: 'GET', headers: this.getHeaders() });
  }
  async getBlogTravelPublic() {
    return this.heroRequest(`${API_BASE_URL}/blog`, { method: 'GET' });
  }
  async updateBlogTravelConfig(data) {
    return this.heroRequest(`${API_BASE_URL}/blog/admin/config`, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify(data) });
  }
  async addBlogSlide(section, formData) {
    const endpoint = section === 'hero'
      ? `${API_BASE_URL}/blog/admin/hero`
      : `${API_BASE_URL}/blog/admin/cards/${section}`;
    return this.heroRequest(endpoint, { method: 'POST', headers: this.getAuthHeader(), body: formData });
  }
  async updateBlogSlide(section, id, formData) {
    const endpoint = section === 'hero'
      ? `${API_BASE_URL}/blog/admin/hero/${id}`
      : `${API_BASE_URL}/blog/admin/cards/${section}/${id}`;
    return this.heroRequest(endpoint, { method: 'PUT', headers: this.getAuthHeader(), body: formData });
  }
  async deleteBlogSlide(section, id) {
    const endpoint = section === 'hero'
      ? `${API_BASE_URL}/blog/admin/hero/${id}`
      : `${API_BASE_URL}/blog/admin/cards/${section}/${id}`;
    return this.heroRequest(endpoint, { method: 'DELETE', headers: this.getHeaders() });
  }
  async toggleBlogSlide(section, id) {
    const endpoint = section === 'hero'
      ? `${API_BASE_URL}/blog/admin/hero/${id}/toggle`
      : `${API_BASE_URL}/blog/admin/cards/${section}/${id}/toggle`;
    return this.heroRequest(endpoint, { method: 'PATCH', headers: this.getHeaders() });
  }
  async reorderBlogSlides(section, slideIds) {
    const endpoint = section === 'hero'
      ? `${API_BASE_URL}/blog/admin/hero/reorder`
      : `${API_BASE_URL}/blog/admin/cards/${section}/reorder`;
    return this.heroRequest(endpoint, { method: 'PUT', headers: this.getHeaders(), body: JSON.stringify({ slideIds }) });
  }

  // ════════════════════════════════════════
  // USER MANAGEMENT METHODS
  // ════════════════════════════════════════

  async getUserStats() {
    return this.heroRequest(`${API_BASE_URL}/admin/users/stats`, { method: 'GET', headers: this.getHeaders() });
  }
  async getUsers({ search = '', filter = 'all', page = 1, limit = 50 } = {}) {
    const params = new URLSearchParams({ search, filter, page, limit }).toString();
    return this.heroRequest(`${API_BASE_URL}/admin/users?${params}`, { method: 'GET', headers: this.getHeaders() });
  }
  async getUserById(id) {
    return this.heroRequest(`${API_BASE_URL}/admin/users/${id}`, { method: 'GET', headers: this.getHeaders() });
  }
  async toggleUserStatus(id) {
    return this.heroRequest(`${API_BASE_URL}/admin/users/${id}/toggle`, { method: 'PATCH', headers: this.getHeaders() });
  }
  async deleteUser(id) {
    return this.heroRequest(`${API_BASE_URL}/admin/users/${id}`, { method: 'DELETE', headers: this.getHeaders() });
  }

  // ════════════════════════════════════════
  // ★ CONTACT MESSAGE METHODS
  // ════════════════════════════════════════

  async getContactMessages({ page = 1, limit = 15, status = '' } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set('status', status);
    return this.heroRequest(`${API_BASE_URL}/admin/contact?${params.toString()}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
  }

  async getContactMessageById(id) {
    return this.heroRequest(`${API_BASE_URL}/admin/contact/${id}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
  }

  async updateContactMessageStatus(id, status) {
    return this.heroRequest(`${API_BASE_URL}/admin/contact/${id}/status`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ status }),
    });
  }

  async deleteContactMessage(id) {
    return this.heroRequest(`${API_BASE_URL}/admin/contact/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
  }
}

const adminApi = new AdminApiService();
export default adminApi;
